import { createSignal, onMount, Show, useContext, For } from 'solid-js';
import { Button } from '../../ui-components/button/button';
import { Text } from '../../ui-components/text/text';
import { Errors, FieldType, FormFieldContext, FormProps, FormValue } from './forms-types';
import { countries } from '../../tools/countries-data';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { validate } from './validation';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { useLocation, useSearchParams } from '@solidjs/router';
import { Heading } from '../../ui-components/heading/heading';
import { validateCaptcha } from '../../tools/validate-captcha';
import { CheckCircleIcon } from '../icons-library/check-circle';
import { EventContext } from '../event/event-context-provider';
import { atosGlobalCountrySlugToCountryCode } from '../../tools/countries-data';
import { getPlaceholder } from '../registration-form/helpers';
import theme from '../../style/theme';
import {
    getUniqueSlug,
    isFieldVisibleForUserType,
    hasUserTypeRestriction,
} from './utils';
import { formsClient, POST_FORM_QUERY } from './constants';
import { createCountryTerms } from './country-terms';
import { FormField } from './forms-fields';
import { PolicyConsent } from './policy';

export const FormsTw = (props: FormProps) => {
    const AppState = useContext(AppContext);
    const eventContext = useContext(EventContext);
    const { isDisabledEvent } = eventContext;
    const { localize, privacyPolicyPage, recaptchaSiteKey, siteInfo, allSiteDomains, productContactFormUrl, termsAndConditions } = AppState; // `allSiteDomains` (unfiltered by siteType) is used for country→siteId/slug lookups so atos-care forms can still resolve atos-medical country sites for per-country consent and terms.
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = createSignal(false);
    const [error, setError] = createSignal('');
    const [errors, setErrors] = createSignal<Errors>({});
    const [success, setSuccess] = createSignal('');
    const [currentStepIndex, setCurrentStepIndex] = createSignal<number>(0);
    let formRef: HTMLDivElement | undefined;

    onMount(() => {
        if (!document.querySelector('#grecaptcha')) {
            const scriptTag = document.createElement('script');
            scriptTag.type = 'text/javascript';
            scriptTag.id = 'grecaptcha';
            scriptTag.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
            document.head.appendChild(scriptTag);
        }
    });

    const nextStepClick = () => {
        const valid = validateFields(props.steps[ currentStepIndex() ].fields);

        if (valid) {
            setCurrentStepIndex(currentStepIndex() + 1);
            formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const previousStepClick = () => {
        // We don't need to validate if the user is going back a step.
        setCurrentStepIndex(currentStepIndex() - 1);
    };

    const isFinalStep = () => currentStepIndex() === props.steps.length - 1;

    const formHasMultipleSteps = () => props.steps.length > 1;

    const setInitialValues = () => {
        const initialObj = props.steps[currentStepIndex()].fields?.reduce((obj: FormValue, field) => {
            const { slug } = field;
            if (slug === 'customDropdown' || slug === 'textArea' || slug === 'textField') {
                const uniqueSlug = getUniqueSlug(field);
                obj[uniqueSlug] = '';
            } else if (field.slug === 'hiddenField') {
                const uniqueSlug = getUniqueSlug(field);
                obj[uniqueSlug] = field.extraSettings?.fieldValue?.value;
            } else {
                obj[field.slug] = '';
            }
            return obj;
        }, {});
        initialObj.acceptPolicy = false;
        // Marketing consent is separate from general privacy policy consent (for double consent countries)
        initialObj.acceptMarketingConsent = false;

        if (siteInfo.siteSlug) {
            // Prepopulate the country field with the value that the site they're visiting has. No matter which step it is in.
            const currentSelectCountryCode = atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug as keyof typeof atosGlobalCountrySlugToCountryCode];
            const currentSelectCountry = countries.find(country => country.code === currentSelectCountryCode);
            if (currentSelectCountry) {
                initialObj.country = currentSelectCountry;
            }
        }
        return initialObj;
    };

    const [formValues, setFormValues] = createSignal<FormValue>(setInitialValues());

    // Check if user is a business lead (HCP or Other)
    const isBusinessLead = () => formValues().userType === 'HCP' || formValues().userType === 'Other';

    const terms = createCountryTerms({
        formValues,
        siteInfo,
        allSiteDomains,
        termsAndConditions,
        localize,
    });
    const { isDoubleConsent } = terms;

    const handleInputChange = (fieldSlug: string, fieldValue: any, customInputSlug?: string) => {
        const newFormValues = { ...formValues() };
        if (fieldSlug === 'country') {
            // We want the whole country object here, not only the code.
            const country = countries.find(country => country.code === fieldValue);
            newFormValues[fieldSlug] = country;
        } else {
            newFormValues[fieldSlug] = fieldValue;
        }

        // If the form has errors, we want to remove it if it's fine now.
        const hasErrors = errors()[fieldSlug];

        setFormValues(newFormValues);

        // When userType changes, clear values for fields that are no longer visible
        if (fieldSlug === 'userType') {
            const clearedValues = { ...newFormValues };
            props.steps[currentStepIndex()].fields.forEach((field) => {
                if (hasUserTypeRestriction(field) && !isFieldVisibleForUserType(field, fieldValue) && field.extraSettings?.name?.value) {
                    delete clearedValues[field.extraSettings.name.value];
                }
            });
            setFormValues(clearedValues);
        }

        if (hasErrors) {
            const field = !customInputSlug ? props.steps[currentStepIndex()].fields.find((field) => field.slug === fieldSlug) : props.steps[currentStepIndex()].fields.find((field) => field.slug === customInputSlug);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    // Eloqua expects these values to look like: {'checkboxGroupName': 'value1; value2; value3'}
    const handleCheckboxChange = (checkboxGroupName: string, checkboxValue: any, checked: boolean) => {
        const newFormValues = { ...formValues() };

        const checkboxValues = newFormValues[checkboxGroupName] ? newFormValues[checkboxGroupName].split(',') : [];
        if (checked) {
            checkboxValues.push(checkboxValue);
        } else {
            const index = checkboxValues.indexOf(checkboxValue);
            checkboxValues.splice(index, 1);
        }
        const checkboxValueString = checkboxValues.join(',');
        newFormValues[checkboxGroupName] = checkboxValueString;

        setFormValues(newFormValues);

        // If the checkbox has errors, we want to remove it if it's fine now.
        const hasErrors = errors()[checkboxGroupName];

        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.extraSettings?.name?.value === checkboxGroupName);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    const handleRadioButtonChange = (radioButtonGroupName: string, value: string) => {
        setFormValues({ ...formValues(), [radioButtonGroupName]: value });

        // If the radiobutton had errors, we want to remove it if it's fine now.
        const hasErrors = errors()[radioButtonGroupName];

        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.extraSettings?.name?.value === radioButtonGroupName);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    const handleDropdownChange = (dropdownGroupName: string, value: string) => {
        setFormValues({ ...formValues(), [dropdownGroupName]: value });

        // If the dropdown had errors, we want to remove it if it's fine now.
        const hasErrors = errors()[dropdownGroupName];

        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.extraSettings?.name?.value === dropdownGroupName);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };


    const handleCheck = (fieldSlug: string) => {
        const newFormValues = { ...formValues() };
        newFormValues[fieldSlug] = !newFormValues[fieldSlug];
        setFormValues(newFormValues);
        const hasErrors = errors()[fieldSlug];
        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.slug === fieldSlug);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    const fieldCtx: FormFieldContext = {
        formValues,
        errors,
        onInputChange: handleInputChange,
        onCheckboxChange: handleCheckboxChange,
        onRadioChange: handleRadioButtonChange,
        onDropdownChange: handleDropdownChange,
        localize,
        siteInfo,
    };

    const isPolicyAccepted = () => {
        const checked = formValues()[ 'acceptPolicy' ];
        let allValid = true;

        if (!checked) {
            const msg = `${localize('please-agree-to-terms', 'Please agree to the terms and conditions before submitting')}`;
            setErrors({ ...errors(), [ 'acceptPolicy' ]: msg });
            allValid = false;
        } else {
            const currentErrors = { ...errors() };
            delete currentErrors[ 'acceptPolicy' ];
            setErrors(currentErrors);
        }

        // Marketing consent is always optional - no validation needed

        return allValid;
    };

    const handleSend = async (e: any) => {
        e.preventDefault();

        setLoading(true);
        setErrors({});
        setError('');

        const extraFields = [];
        if (isBusinessLead()) {
            extraFields.push({
                name: 'Company',
                slug: 'company',
                required: true,
            });
        }

        // Check if the final step's fields are valid
        const validFields = validateFields([...props.steps[currentStepIndex()].fields, ...extraFields]);
        if (!isPolicyAccepted() || !validFields) {
            setLoading(false);
            return;
        }

        try {
            const captchaToken = await validateCaptcha('submit_form', recaptchaSiteKey);            

            if (!captchaToken) {
                throw new Error('Captcha validation failed - probably a bot.');
            }

            const currentSite = allSiteDomains.find((domain) => domain.siteId === siteInfo.siteId);
            const productForm = productContactFormUrl?.includes(location.pathname);

            // All forms on event pages should use Eloqua event form
            const isEventForm = eventContext.isEventPage;

            // Eloqua requires eventId for default event forms - block submission if missing
            // Custom campaigns may have different requirements, so skip validation for those
            if (isEventForm && !eventContext.eventId && !props.formCampaignId) {
                console.error('Event form submission blocked: missing eventId. Check that eventDate is set in WordPress.');
                setError(localize('form-submit-error', 'Error submitting the form'));
                setLoading(false);
                return;
            }

            let formType;
            if (siteInfo.siteType === 'atos-care') {
                formType = isEventForm ? 'goto-event' : 'contactAtosCare';
            } else {
                if (isEventForm) {
                    formType = 'goto-event';
                } else if (productForm) {
                    formType = 'product';
                } else {
                    formType = 'contact';
                }
            }

            const formAnswers = { ...formValues() };
            // Remove consent fields from form data (they're handled separately)
            delete formAnswers.acceptPolicy;
            delete formAnswers.acceptMarketingConsent;

            // For double consent, include explicit consent values
            const consentData = isDoubleConsent() ? {
                generalConsent: formValues().acceptPolicy ? 'Yes' : 'No',
                marketingConsent: formValues().acceptMarketingConsent ? 'Yes' : 'No',
            } : {};

            const variables = {
                type: formType,
                referer: window.location.href,
                siteCountry: currentSite?.country || '',
                captchaToken,
                gotoEventId: eventContext.eventId,
                sessionId: eventContext.selectedSessionId,
                input: {
                    formData: {
                        ...formAnswers,
                        ...consentData,
                        ...searchParams?.product && { leadHook: searchParams?.product }, // If we have a product in the url, we add it to the form data as a lead hook.
                    },
                },
                campaignId : props.formCampaignId,
            };

            const res: any = await formsClient.request(POST_FORM_QUERY, variables);

            if (res.postForm.success) {
                // Optimistically decrement the chosen session spotsLeft (cosmetic) on success.
                if (eventContext.selectedSessionId) {
                    eventContext.decrementSelectedSpotsLeft?.();
                }
                formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setSuccess(localize('form-submit-success', 'Form submitted'));
                setFormValues(setInitialValues());
            } else if (res.postForm.reason === 'session-full' || res.postForm.reason === 'session-closed') {
                // The seat was lost between page load and submit; prompt for another session and refetch live availability.
                formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setError(localize('session-just-filled', 'This session just filled up — please pick another'));
                eventContext.refetchSessions?.();
            } else if (res.postForm.reason === 'session-not-found') {
                // The session was deleted; distinct message, refetch so the stale row disappears.
                formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setError(localize('session-unavailable', 'This session is no longer available — please pick another'));
                eventContext.refetchSessions?.();
            } else {
                throw new Error();
            }
        } catch (e) {
            console.log('Error submitting form: ', e);
            formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setError(localize('form-submit-error', 'Error submitting the form'));
        }

        setLoading(false);
    };


    const validateFields = (fields: FieldType[]) => {
        setErrors({}); // Reset errors before validation

        fields.forEach((field) => {
            const { slug } = field;
            if (slug === 'customCheckbox' || slug === 'customDropdown' || slug === 'customRadioButtons' || slug === 'customTextField' || slug === 'textArea') {
                // Skip validation for user-type restricted fields when not visible
                if (hasUserTypeRestriction(field) && !isFieldVisibleForUserType(field, formValues().userType)) {
                    return;
                }
                const { extraSettings, required } = field;
                if (!extraSettings) return;
                const { name } = extraSettings;
                const value = formValues()[name.value];
                if (required && (!value || value.length === 0)) {
                    let fieldName = '';

                    if (field.slug === 'customCheckbox') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'customDropdown') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'customRadioButtons') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'customTextField') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'textArea') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    }
                    
                    const translation = getPlaceholder({...field, name: fieldName, required: false}, localize);

                    const msg = `${translation} ${localize('is-required', 'is required')}`;
                    setErrors({ ...errors(), [name.value]: msg });
                } else {
                    const currentErrors = { ...errors() };
                    delete currentErrors[name.value];
                    setErrors(currentErrors);
                }
            } else {
                const key = field.instance ? `${field.slug}-${field.instance}` : field.slug;
                const value = formValues()[key];

                if (field.required && (!value || (typeof value === 'string' && !value.trim()))) {
                    const translation = getPlaceholder({...field, required: false}, localize);
                    const msg = `${translation} ${localize('is-required', 'is required')}`;
                    setErrors({ ...errors(), [field.slug]: msg });
                    return false;
                }

                // Not required but no value is fine.
                if (!value) {
                    return;
                }

                // If we have a value, we validate it.
                const { valid, errorMessage } = validate(key, value, localize);
                if (errorMessage) {
                    setErrors({ ...errors(), [field.slug]: errorMessage });
                } else if (valid) {
                    const currentErrors = { ...errors() };
                    delete currentErrors[field.slug];
                    setErrors(currentErrors);
                }
            }
        });

        // Special case: Company field injected programmatically
        if (isBusinessLead()) {
            const stepIncludesUserType = props.steps[currentStepIndex()].fields.some(
                (field: FieldType) => field.slug === 'userType'
            );
            // a double check to make sure the current dom actually has a company input
            const companyInput = document.querySelector<HTMLInputElement>('[name="company"]');
            if (stepIncludesUserType && companyInput) {
                const value = formValues()['company'];
                if (!value || !value.trim()) {
                    setErrors({
                        ...errors(),
                        company: `${localize('company', 'Company')} ${localize('is-required', 'is required')}`,
                    });
                    return false;
                }
            }
        }

        if (Object.keys(errors()).length > 0) {
            return false;
        }

        return true;
    };

    const renderCompanyField = () => {
        // If user selects HCP or Other (i.e. a "business lead"), the company field should be required/mandatory.
        // However, if it's not even added in the WP form we need to manually add it down here.
        if (!isBusinessLead()) return null;

        // Check all steps for the existance of the company field
        const formHasCompanyField = props.steps.some(step =>
            step.fields.some((field: FieldType) => field.slug === 'company')
        );

        if (formHasCompanyField) {
            // If the company field is already present, we don't need to render it again.
            return null;
        }

        const stepIncludesUserType = props.steps[currentStepIndex()].fields.some((field: FieldType) => {
            return field.slug === 'userType';
        });

        if (!stepIncludesUserType) {
            // If the userType field is not in the current step, we don't need to render the company field. This avoids rendering the company field on every step.
            return null;
        }

        const field: FieldType = {
            slug: 'company',
            name: 'Company',
            required: true,
            extraSettings: {
                translation: { value: '' },
                info: { value: '' },
                link: { value: '' },
                linkText: { value: '' },
                placeholder: { value: 'Company' },
                name: { value: 'company' },
                options: { value: '' },
                fieldValue: { value: '' },
                hcpOnly: { value: '' },
                patientOnly: { value: '' },
            },
        };
        return <FormField field={field} ctx={fieldCtx} stepFields={[]} forceRequire={() => true} />;
    };

    const productContactTitle = () => {
        // This ensures that the pre- and post-text are only added if they are not empty, taking into account required spaces.
        const preTextTranslationExists = localize('product-contact-form-pre-text', '') !== '';
        const preText = preTextTranslationExists ? `${localize('product-contact-form-pre-text', '')} ` : '';
        const postText = localize('product-contact-form-post-text', '');

        return `${preText + searchParams?.product} ${postText}`;
    };

    const StepFields = () => (
        <For each={props.steps[currentStepIndex()].fields}>{(field: FieldType) => {
            const { slug } = field;
            const stepFields = props.steps[currentStepIndex()].fields;

            // We don't want to render the policy field here, as it's rendered separately.
            if (slug === 'acceptPolicy') {
                return null;
            }

            // Handle the special cases first.
            if (slug === 'company') {
                return <FormField field={field} ctx={fieldCtx} stepFields={stepFields} forceRequire={isBusinessLead} />;
            }

            // Render the injected Company field directly after userType so it appears
            // right below the user type dropdown (instead of at the bottom of the form).
            if (slug === 'userType') {
                return (
                    <>
                        <FormField field={field} ctx={fieldCtx} stepFields={stepFields} />
                        {renderCompanyField()}
                    </>
                );
            }

            // Hide user-type restricted fields unless the matching type is selected.
            // Uses <Show> because <For> callbacks are not reactive tracking scopes in SolidJS.
            if (hasUserTypeRestriction(field)) {
                return (
                    <Show when={isFieldVisibleForUserType(field, formValues().userType)}>
                        <FormField field={field} ctx={fieldCtx} stepFields={stepFields} />
                    </Show>
                );
            }

            return <FormField field={field} ctx={fieldCtx} stepFields={stepFields} />;
        }}</For>
    );

    const NavigationButtons = () => (
        <div class="flex justify-between items-center mt-cvi-6">
            <Show when={currentStepIndex() !== 0} fallback={<div />}>
                <Button
                    label={localize('previous', 'Previous')}
                    variant='tertiary'
                    onClick={previousStepClick}
                />
            </Show>

            <Show
                when={!isFinalStep()}
                fallback={
                    <Button
                        label={eventContext.sessionAvailabilityLoading ? localize('loading-availability', 'Loading availability…') : localize('form-submit', 'Submit')}
                        variant='primary'
                        onClick={handleSend}
                        disabled={loading() || eventContext.sessionAvailabilityLoading}
                    />
                }
            >
                <Button
                    label={localize('next', 'Next')}
                    variant='primary'
                    onClick={nextStepClick}
                />
            </Show>
        </div>
    );

    const FinalStepFixedElements = () => (
        <div class="flex flex-col gap-cvi-5">
            <PolicyConsent
                formValues={formValues}
                errors={errors}
                onCheck={handleCheck}
                localize={localize}
                privacyPolicyPage={privacyPolicyPage}
                terms={terms}
            />

            <Show when={error()} fallback={<div />}>
                <div class="flex items-center justify-center h-cvi-13 label-md font-cvi-regular text-cvi-fg-danger">{error()}</div>
            </Show>

            <Show when={loading()} fallback={<div/>}>
                <div class="flex items-center justify-center h-cvi-13">
                    <LoadingSpinner />
                </div>
            </Show>
        </div>
    );

    const calculatePercentCompleted = () => {
        // Returns the amount the progress line should be filled in percent.
        const totalSteps = props.steps.length;
        const newTotal = totalSteps - 1;
        const percent = Math.round((currentStepIndex() / newTotal) * 100);
        
        return percent;
    };

    const dynamicProgressBarWidth = () => {
        // Adjusts width based on the number of steps.
        if (props.steps.length > 4) {
            return '100%';
        }

        const width = 40 + (props.steps.length * 10);
        return `${width}%`;
    };

    const StepOverview = () => (
        <div class="
                        flex flex-row justify-between items-center gap-4 relative mx-auto mb-[6.5rem]
                        max-tablet:w-full max-tablet:mb-[4rem]
                        tablet:w-[var(--dynamic-width)]
                        before:content-[''] before:w-full before:h-[4px] before:bg-gray-light
                        before:absolute before:bottom-[calc(50%-2px)] before:left-0 before:z-0
                        after:content-[''] after:h-[4px] after:bg-primary 
                        after:absolute after:bottom-[calc(50%-2px)] after:left-0 after:z-0 
                        after:transition-[width] after:duration-200 after:ease-linear
                        after:w-[var(--after-width)]
                    "
        style={{
            '--dynamic-width': dynamicProgressBarWidth(),
            '--after-width': `${calculatePercentCompleted()}%`                                           
        }}
        >
            <For each={props.steps}>{(step, index) => {
                    
                return (
                    <div class="flex relative flex-col items-center gap-[0.5rem]">
                        <div class="z-[1] w-[4.5rem] h-[4.5rem] min-w-[4.5rem] min-h-[4.5rem] max-w-[4.5rem] max-h-[4.5rem] flex justify-center items-center p-0 rounded-full border-[3px] transition-all duration-200 ease-in-out"
                            classList={{
                                'bg-primary': index() < currentStepIndex(),
                                'text-white': index() < currentStepIndex(),
                                'border-primary': index() <= currentStepIndex(),
                                'bg-white': index() >= currentStepIndex(),
                                'text-gray-dark': index() >= currentStepIndex(),
                                'border-gray-light': index() > currentStepIndex(),
                                'delay-0': index() < currentStepIndex(),
                                'delay-200': index() >= currentStepIndex(),
                            }}
                        >
                            <Text fontSize={'normal'} color='defer' noBlockSpacing={true}>
                                {`${index() + 1}`}
                            </Text>
                        </div>

                        <Show when={step.stepName && !props.hideStepNames}>
                            <div class="absolute top-[4.5rem] mt-[0.5rem] w-max text-center max-w-[12rem] max-tablet:hidden">
                                <Text fontSize='footerSmall' color={theme.palette.darkGray}>
                                    {step.stepName}
                                </Text>
                            </div>
                        </Show>
                    </div>
                );
            }}</For>
        </div>
    );

    const Step = () => (
        <>
            <StepHeading />

            <div class="flex flex-col gap-cvi-6">
                <StepFields />
            </div>
        </>
    );

    const StepHeading = () => (
        <Show when={formHasMultipleSteps() && props.steps[ currentStepIndex() ].stepName && !props.hideStepNames}>
            <Heading tag='h3'>
                {props.steps[ currentStepIndex() ]?.stepName}
            </Heading>
        </Show>
    );

    const renderSuccessMessage = () => {
        return (
            <div class="p-l tablet:p-xxxl flex flex-col items-center bg-white rounded-[var(--radius-large)]">
                <div class="mb-s w-[80px] h-[80px] tablet:w-[100px] tablet:h-[100px]">
                    <CheckCircleIcon height="100%" width="100%" />
                </div>
                {props.successMessageTitle && (
                    <Heading tag="h4" variant="large" align="center" noBlockSpacing={false}>
                        {props.successMessageTitle}
                    </Heading>
                )}
                <Text noBlockSpacing={false} align="center" fontSize="normal">
                    {props.successMessageText
                        ? props.successMessageText
                        : localize(
                            'form-submit-success',
                            'The form was successfully submitted'
                        )}
                </Text>
            </div>
        );
    };

    return (
        <ErrorCatcher componentName='Forms'>
            <Show when={success()}>{renderSuccessMessage()}</Show>

            {/* Only render the form if the privacy policy page is set and the fields are loaded */}
            <Show when={!success() && privacyPolicyPage && props.steps[currentStepIndex()].fields && !isDisabledEvent }>
                <div class="w-full max-w-[65rem] scroll-mt-[10rem] mt-[5rem] first:mt-0" ref={formRef}>
                    <Show when={searchParams?.product}>
                        <Text noBlockSpacing={false} fontSize='normal' fontStyle='italic' color='darkestGray' displayRedVerticalLine={false}>
                            {productContactTitle()}
                        </Text>
                    </Show>

                    <Show when={formHasMultipleSteps()}>
                        <StepOverview />
                    </Show>

                    <form class="flex flex-col gap-cvi-6 mb-cvi-15 max-mobile:justify-center max-mobile:m-0">
                        <Step/>

                        <p class="m-0 label-md font-cvi-regular text-cvi-fg-default">{`* ${localize('required-fields', 'required fields')}`}</p>

                        <Show when={isFinalStep()}>
                            <FinalStepFixedElements />
                        </Show>

                        <div class="mt-cvi-4">
                            <Show
                                when={formHasMultipleSteps()}
                                fallback={(
                                    <Button
                                        label={eventContext.sessionAvailabilityLoading ? localize('loading-availability', 'Loading availability…') : localize('form-submit', 'Submit')}
                                        variant='primary'
                                        onClick={(e) => handleSend(e)}
                                        customCss='margin-left: auto;'
                                        disabled={loading() || eventContext.sessionAvailabilityLoading}
                                        type="submit"
                                    />
                                )}
                            >
                                <NavigationButtons />
                            </Show>
                        </div>
                    </form>
                </div>
            </Show>
        </ErrorCatcher>
    );
};

